/**
 * The dashboard's live feed (/api/dashboard/live, DashboardLive Durable Object) for the People page: drives the
 * Live badge (§0.6 connection states: Snapshot → Live → Reconnecting…, backoff 1 s → 30 s with jitter, retry on
 * `online` and tab focus) and delivers "testimony.live" events for the office constituent alert (card 8).
 * Opened after paint; the page never waits for it. The hub sends each viewer only their audience's events
 * (dashboard-live.js publicView): "testimony.live" and the model's raw output reach offices and the platform team
 * only, so for residents and lobbyists this feed just drives the badge.
 */
import { connectionStore } from "../stores/connectionStore";

export interface TestimonyLive { type: "testimony.live"; meetingId: string; t: number; ward: number | null; body: string; topic?: string }

export function openPeopleFeed(onTestimony: (e: TestimonyLive) => void) {
  let ws: WebSocket | null = null, stopped = false, attempts = 0, timer = 0, offlineSince = 0;
  const connect = () => {
    if (stopped) return;
    connectionStore.setState({ status: attempts ? "reconnecting" : "connecting" });
    ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/api/dashboard/live`);
    ws.onopen = () => { attempts = 0; offlineSince = 0; connectionStore.setState({ status: "live", attempts: 0 }); };
    ws.onmessage = (m) => {
      try {
        const msg = JSON.parse(String(m.data));
        if (msg.kind === "event" && msg.event?.type === "testimony.live") onTestimony(msg.event as TestimonyLive);
      } catch { /* not ours */ }
    };
    ws.onclose = () => {
      if (stopped) return;
      offlineSince ||= Date.now();
      attempts++;
      // Offline for more than 30 s: back to the snapshot badge.
      connectionStore.setState({ status: Date.now() - offlineSince > 30_000 ? "snapshot" : "reconnecting", attempts });
      const wait = Math.min(30_000, 1000 * 2 ** (attempts - 1)) * (0.8 + Math.random() * 0.4);
      timer = window.setTimeout(connect, wait);
    };
  };
  const retry = () => { if (ws?.readyState === WebSocket.CLOSED) { clearTimeout(timer); connect(); } };
  addEventListener("online", retry);
  addEventListener("focus", retry);
  const start = () => connect();
  if ("requestIdleCallback" in window) requestIdleCallback(start); else setTimeout(start, 0);
  return () => { stopped = true; clearTimeout(timer); removeEventListener("online", retry); removeEventListener("focus", retry); ws?.close(); };
}
